import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LoginResponseDto, PlatformUserDto } from '@eskoolhub/types';
import { api, setAccessToken, setUnauthenticatedHandler } from '../lib/api';

interface AuthState {
  user: PlatformUserDto | null;
  /** True until the initial refresh-cookie check finishes. */
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResponseDto>;
  verifyTotp: (challengeToken: string, code: string) => Promise<PlatformUserDto>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PlatformUserDto | null>(null);
  const [loading, setLoading] = useState(true);

  // On boot, try the refresh cookie so a reload doesn't force a re-login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const refreshed = await api.refresh();
      if (cancelled) return;
      if (refreshed) {
        try {
          setUser(await api.get<PlatformUserDto>('/api/auth/me'));
        } catch {
          setUser(null);
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setAccessToken(null);
      setUser(null);
    });
    return () => setUnauthenticatedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.post<LoginResponseDto>('/api/auth/login', { email, password });
    if (result.status === 'AUTHENTICATED') {
      setAccessToken(result.accessToken);
      setUser(result.user);
    }
    return result;
  }, []);

  const verifyTotp = useCallback(async (challengeToken: string, code: string) => {
    const result = await api.post<Extract<LoginResponseDto, { status: 'AUTHENTICATED' }>>(
      '/api/auth/totp/verify',
      { challengeToken, code },
    );
    setAccessToken(result.accessToken);
    setUser(result.user);
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, verifyTotp, logout }),
    [user, loading, login, verifyTotp, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
